import React, { useEffect, useRef } from 'react';
import { Rnd } from 'react-rnd';
import { accrocher, ciblesDuCadre, SEUIL_MAGNETISME_PX } from '../utils/magnetisme';
import { useLabelStore } from '../store/useLabelStore';
import { orderedLeaves, descendantIds } from '../utils/layerTree';
import { saveToGallery } from '../api/galleryClient';
import ViewDPad from './canvas/ViewDPad';
import BarcodeElement from './canvas/BarcodeElement';
import ImageLayer from './canvas/ImageLayer';
import EraserSurface from './canvas/EraserSurface';
import MarqueeSurface from './canvas/MarqueeSurface';
import { appliquerSelection, marqueeUtile, FORMES } from '../utils/selection';
import ShapeLayer from './canvas/ShapeLayer';
import TextLayer from './canvas/TextLayer';
import DimensionsHUD from './canvas/DimensionsHUD';
import { getCssGradient, getShadowParams, getShadowStyle, getInsetShadowStyle } from './canvas/helpers';

// Un nœud de GROUPE n'a pas de géométrie propre (cf. createGroupFromSelection) :
// lui appliquer un delta lui écrivait `x: undefined + dx` = NaN et laissait ses
// feuilles sur place, donc la composition se disloquait. Tout identifiant qui
// entre dans un déplacement doit donc être détendu en feuilles d'abord — y
// compris ceux qui arrivent par un LIEN, car un groupe peut porter un linkedId
// (« tout sélectionner » dans le panneau des calques prend les groupes, et
// linkElements ne les exclut pas).
function ajouterFeuilles(elements, id, cible) {
    const el = elements.find(x => x.id === id);
    if (!el) return;
    if (el.type === 'group') {
        descendantIds(elements, id).forEach(d => {
            const de = elements.find(x => x.id === d);
            if (de && de.type !== 'group') cible.add(d);
        });
    } else {
        cible.add(id);
    }
}

export default function LabelCanvas() {
    const {
        canvas, elements, aiImage, selectedElementIds,
        selectElement, updateElement, setCanvas
    } = useLabelStore();
    const activeTool = useLabelStore((s) => s.activeTool);

    const [isShiftPressed, setIsShiftPressed] = React.useState(false);
    const [isResizingSide, setIsResizingSide] = React.useState(false);
    const [pan, setPan] = React.useState({ x: 0, y: 0 });
    const [isSpacePressed, setIsSpacePressed] = React.useState(false);
    const [isDragging, setIsDragging] = React.useState(false);
    const dragStartRef = React.useRef({ x: 0, y: 0 });

    // LA MAIN EST-ELLE AUX COMMANDES ? Un OU exclusif, et cette forme dit
    // exactement la règle : la barre d'espace ne « donne » pas la main, elle
    // BASCULE. Depuis n'importe quel autre outil, la tenir déplace la vue le
    // temps d'un geste — le comportement historique. Depuis l'outil Main, elle
    // fait l'inverse et rend la flèche, pour attraper un calque sans quitter la
    // main. Écrit en deux conditions séparées, l'un des quatre cas se serait
    // perdu tôt ou tard.
    const mainAuxCommandes = (activeTool === 'hand') !== isSpacePressed;
    // L'outil qui gouverne réellement les calques. En Main + espace, c'est la
    // flèche : sélectionner, déplacer, redimensionner.
    const outilEffectif = mainAuxCommandes ? 'hand'
        : activeTool === 'hand' ? 'select'
        : activeTool;

    // Smart guides — lignes bleues affichées en temps réel pendant le drag d'un
    // calque, quand il s'aligne avec un autre calque, un guide ou le centre du
    // canvas. Style Figma. Vide en dehors d'un drag.
    const [smartGuides, setSmartGuides] = React.useState({ vertical: [], horizontal: [] });

    // ID du calque texte actuellement en édition inline (double-clic).
    // Quand non null : Tiptap est monté éphémèrement sur ce calque, TEXT_SCALE
    // est désactivé, les overlays de sélection sont masqués.
    const [editingId, setEditingId] = React.useState(null);
    // Refs sur les wrappers de chaque calque texte (pour positionner la toolbar)
    const textContainerRefs = React.useRef({});

    // Sortir du mode édition uniquement si le calque édité est SUPPRIMÉ.
    // Avant on fermait aussi sur déselection (clic ailleurs) → comportement
    // frustrant si on voulait juste cliquer en dehors pour voir le rendu.
    // Désormais : l'éditeur reste ouvert tant qu'on n'a pas explicitement
    // validé (bouton ✓ dans la toolbar) ou pressé Escape. Le calque édité
    // est re-sélectionné automatiquement pour rester cohérent visuellement.
    React.useEffect(() => {
        if (!editingId) return;
        const stillExists = elements.some(e => e.id === editingId);
        if (!stillExists) {
            setEditingId(null);
            return;
        }
        // Garde le calque édité sélectionné même si l'utilisateur a cliqué
        // ailleurs (pour que la toolbar reste cohérente).
        if (!selectedElementIds.includes(editingId)) {
            selectElement(editingId);
        }
    }, [editingId, elements, selectedElementIds, selectElement]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Shift') setIsShiftPressed(true);
            if (e.code === 'Space' && !isSpacePressed) {
                if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) || document.activeElement?.isContentEditable) return;
                // Tiptap (édition inline) utilise un <div contenteditable> qui n'est
                // ni INPUT ni TEXTAREA. Sans ce check, e.preventDefault() bloquait
                // la touche Espace dans l'éditeur → impossible de taper d'espaces.
                if (document.activeElement?.isContentEditable) return;
                e.preventDefault();
                setIsSpacePressed(true);
            }
            // Raccourci pour supprimer un ou plusieurs calques sélectionnés.
            // Suppression directe sans confirmation : si erreur, Ctrl+Z restaure.
            if (e.key === 'Delete' || e.key === 'Backspace') {
                if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) || document.activeElement?.isContentEditable) return;
                if (document.activeElement?.isContentEditable) return;
                const state = useLabelStore.getState();

                // Outil de zone : Suppr efface la ZONE, et ne supprime JAMAIS le
                // calque — la branche se termine par un `return` inconditionnel.
                //
                // La première version ne rendait la main que lorsqu'une zone
                // existait, et retombait sinon sur la suppression d'éléments : un
                // Suppr sans zone tracée faisait donc disparaître l'image entière,
                // exactement ce que ce garde-fou prétendait empêcher. Le
                // commentaire affirmait le contraire du code.
                if (state.activeTool === 'marquee') {
                    // Retrait du dernier sommet d'un polygone en cours.
                    const brouillon = state.marqueeDraft;
                    if (e.key === 'Backspace' && brouillon?.points?.length) {
                        e.preventDefault();
                        const pts = brouillon.points.slice(0, -1);
                        state.setMarqueeDraft(pts.length ? { ...brouillon, points: pts } : null);
                        return;
                    }
                    const zone = state.marquee;
                    // La zone doit appartenir à un calque ENCORE sélectionné :
                    // sinon l'utilisateur qui a changé de calque effacerait une
                    // zone qu'il ne voit plus, sur une image qu'il ne regarde pas.
                    const cible = zone && state.selectedElementIds.includes(zone.elementId)
                        ? state.elements.find((el) => el.id === zone.elementId)
                        : null;
                    if (zone && cible) {
                        e.preventDefault();
                        appliquerSelection(cible, zone, {
                            feather: state.selection.feather,
                            exterieur: false,
                        }).then((mask) => {
                            if (!mask) return;
                            const st = useLabelStore.getState();
                            st.updateElement(cible.id, { eraseMask: mask });
                            st._pushState('Effacement de la sélection');
                        });
                        return;
                    }
                    // Pas de zone exploitable : on ne fait RIEN. Supprimer le
                    // calque serait une destruction silencieuse, et l'outil actif
                    // dit assez que l'utilisateur travaille dans l'image, pas sur
                    // la liste des calques. Pour supprimer un calque, il repasse
                    // par la flèche (V) ou par le panneau Calques.
                    return;
                }

                if (state.selectedElementIds.length > 0) {
                    e.preventDefault();
                    state.removeElements(state.selectedElementIds);
                }
            }

            // --- Déplacement précis au clavier ---
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) || document.activeElement?.isContentEditable) return;
                const state = useLabelStore.getState();
                const selected = state.elements.filter(el => state.selectedElementIds.includes(el.id) && !el.locked);
                if (selected.length === 0) return;
                e.preventDefault();

                // Précision par défaut : 0.1 mm (mouvement fin).
                // Shift = 1 mm (mouvement standard).
                // Shift + Alt/Option = 10 mm (gros mouvement).
                let step = 0.1;
                if (e.shiftKey && e.altKey) step = 10;
                else if (e.shiftKey) step = 1;

                const dx = e.key === 'ArrowRight' ? step : e.key === 'ArrowLeft' ? -step : 0;
                const dy = e.key === 'ArrowDown' ? step : e.key === 'ArrowUp' ? -step : 0;

                selected.forEach(el => {
                    state.updateElement(el.id, {
                        x: Math.round((el.x + dx) * 10) / 10,
                        y: Math.round((el.y + dy) * 10) / 10
                    });
                });
                // Debounce pushState pour les mouvements rapides (maintenir la touche enfoncée)
                clearTimeout(window._arrowPushTimeout);
                window._arrowPushTimeout = setTimeout(() => {
                    useLabelStore.getState()._pushState('Déplacement clavier');
                }, 300);
            }

            // --- Entrée : fermer le lasso polygonal en cours ---
            if (e.key === 'Enter') {
                if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)
                    || document.activeElement?.isContentEditable) return;
                const st = useLabelStore.getState();
                if (st.activeTool === 'marquee' && st.marqueeDraft) {
                    e.preventDefault();
                    if (marqueeUtile(st.marqueeDraft)) st.setMarquee(st.marqueeDraft);
                    else st.clearMarquee();
                    return;
                }
            }

            // --- Échap : revenir à la Sélection ---
            // Le réflexe de sortie d'un mode. Sans ça, la seule issue serait de
            // retrouver la palette et d'y cliquer.
            //
            // En mode zone, Échap se dégrade en trois temps : il abandonne
            // d'abord le tracé en cours, puis la zone tracée, et seulement
            // ensuite l'outil. Sans cette cascade, un Échap réflexe pour annuler
            // un polygone raté coûterait aussi la sortie de l'outil.
            if (e.key === 'Escape' && useLabelStore.getState().activeTool !== 'select') {
                if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)
                    || document.activeElement?.isContentEditable) return;
                const st = useLabelStore.getState();
                if (st.activeTool === 'marquee' && (st.marqueeDraft || st.marquee)) {
                    st.clearMarquee();
                    return;
                }
                st.setActiveTool('select');
                return;
            }

            // --- V / E / M / H / Tab : outils (conventions Photoshop & Illustrator) ---
            // Touches nues, donc on écarte tout modificateur pour ne pas
            // marcher sur Ctrl+V, Cmd+E ou la tabulation de formulaire.
            if (!e.ctrlKey && !e.metaKey && !e.altKey
                && ['KeyV', 'KeyE', 'KeyM', 'KeyH', 'Tab'].includes(e.code)) {
                const ae = document.activeElement;
                if (['INPUT', 'TEXTAREA', 'SELECT'].includes(ae?.tagName) || ae?.isContentEditable) return;
                // Tab n'est détourné que si RIEN n'a le focus. Dès que
                // l'utilisateur navigue au clavier, un élément est focalisé et
                // la tabulation reprend son rôle : le raccourci ne doit pas
                // coûter l'accessibilité au clavier de toute l'application.
                //
                // Maj+Tab n'est JAMAIS détourné. AVANT, le garde écartait Ctrl,
                // Cmd et Alt mais pas Maj : quand rien n'avait le focus, un
                // Maj+Tab basculait la palette d'outils au lieu de remonter dans
                // l'ordre de tabulation, et il n'existait alors plus aucun moyen
                // de revenir en arrière au clavier. Maj reste toléré sur les
                // lettres, où il ne recouvre aucune fonction du navigateur (et où
                // Maj+M garde ainsi le défilement des formes, comme Photoshop).
                if (e.code === 'Tab' && (e.shiftKey || (ae && ae !== document.body))) return;
                e.preventDefault();
                const st = useLabelStore.getState();
                if (e.code === 'KeyV') st.setActiveTool('select');
                else if (e.code === 'KeyE') st.setActiveTool('eraser');
                else if (e.code === 'KeyM') {
                    // M active l'outil de zone ; M à nouveau fait défiler les
                    // quatre formes, comme Maj+M dans Photoshop. Un seul doigt
                    // sur une seule touche, et la barre suit.
                    if (st.activeTool === 'marquee') {
                        const i = FORMES.indexOf(st.selection.shape);
                        st.setSelection({ shape: FORMES[(i + 1) % FORMES.length] });
                    } else {
                        st.setActiveTool('marquee');
                    }
                }
                else if (e.code === 'KeyH') st.setActiveTool('hand');
                else st.setToolPalette({ open: !st.toolPalette.open });
                return;
            }

            // --- Ctrl+Z : Undo ---
            if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
                if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) || document.activeElement?.isContentEditable) return;
                e.preventDefault();
                useLabelStore.getState().undo();
            }

            // --- Ctrl+Y / Ctrl+Shift+Z : Redo ---
            if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
                if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) || document.activeElement?.isContentEditable) return;
                e.preventDefault();
                useLabelStore.getState().redo();
            }

            // --- Ctrl+D : Dupliquer ---
            if ((e.ctrlKey || e.metaKey) && e.key === 'd') {
                if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) || document.activeElement?.isContentEditable) return;
                e.preventDefault();
                const state = useLabelStore.getState();
                if (state.selectedElementIds.length > 0) {
                    state.duplicateElements(state.selectedElementIds);
                    setTimeout(() => useLabelStore.getState()._pushState('Duplication'), 0);
                }
            }

            // --- Ctrl+A : Tout sélectionner ---
            if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
                if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) || document.activeElement?.isContentEditable) return;
                e.preventDefault();
                const state = useLabelStore.getState();
                const allIds = state.elements.filter(el => el.type !== 'guide' && !el.hidden).map(el => el.id);
                useLabelStore.setState({ selectedElementIds: allIds });
            }

            // --- Ctrl+Shift+G : Dégrouper (à tester AVANT Ctrl+G) ---
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'g' || e.key === 'G')) {
                if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) || document.activeElement?.isContentEditable) return;
                e.preventDefault();
                useLabelStore.getState().ungroupSelection();
                return;
            }
            // --- Ctrl+G : Grouper (groupe hiérarchique façon Illustrator) ---
            if ((e.ctrlKey || e.metaKey) && (e.key === 'g' || e.key === 'G') && !e.shiftKey) {
                if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) || document.activeElement?.isContentEditable) return;
                e.preventDefault();
                const state = useLabelStore.getState();
                if (state.selectedElementIds.length >= 1) {
                    state.createGroupFromSelection();
                }
            }

            // --- Cmd/Ctrl+Alt+F : Ajuster le bloc au contenu (façon InDesign) ---
            // (Cmd+Alt+C d'InDesign ouvre la console DevTools dans Chrome → F comme Fit.)
            if ((e.ctrlKey || e.metaKey) && e.altKey && (e.code === 'KeyF')) {
                if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) || document.activeElement?.isContentEditable) return;
                e.preventDefault();
                import('../utils/fitFrame').then(({ fitSelectionToContent }) => {
                    fitSelectionToContent(useLabelStore.getState());
                });
                return;
            }

            // --- F5 : Mode Présentation ---
            if (e.key === 'F5') {
                e.preventDefault();
                // Dispatch custom event that EditorView listens for
                window.dispatchEvent(new CustomEvent('labelforge:presentation'));
            }

            // --- Ctrl+C : Copier ---
            if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
                if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) || document.activeElement?.isContentEditable) return;
                const state = useLabelStore.getState();
                if (state.selectedElementIds.length > 0) {
                    window._labelforgeClipboard = state.elements
                        .filter(el => state.selectedElementIds.includes(el.id))
                        .map(el => JSON.parse(JSON.stringify(el)));
                }
            }

            // --- Ctrl+V : Coller ---
            if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
                if (['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName) || document.activeElement?.isContentEditable) return;
                if (window._labelforgeClipboard && window._labelforgeClipboard.length > 0) {
                    e.preventDefault();
                    const state = useLabelStore.getState();
                    const newIds = [];
                    window._labelforgeClipboard.forEach(el => {
                        const newId = 'el_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5);
                        state.addElement({ ...el, id: newId, x: el.x + 3, y: el.y + 3, name: (el.name || 'Calque') + ' (copie)', linkedId: null });
                        newIds.push(newId);
                    });
                    useLabelStore.setState({ selectedElementIds: newIds });
                    setTimeout(() => useLabelStore.getState()._pushState('Collage'), 0);
                }
            }
        };
        const handleKeyUp = (e) => {
            if (e.key === 'Shift') setIsShiftPressed(false);
            if (e.code === 'Space') {
                setIsSpacePressed(false);
                setIsDragging(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [isSpacePressed]);

    const handleWheel = (e) => {
        // Zoom MULTIPLICATIF proportionnel au delta réel (façon Figma).
        // L'ancien pas fixe de 10 % PAR ÉVÉNEMENT s'emballait sur les
        // trackpads/souris Apple, qui envoient des rafales de micro-deltas :
        // impossible d'ajuster finement. Ici :
        // - deltaMode 1 = molette en « lignes » (Firefox) → conversion en px ;
        // - pincement trackpad (ctrlKey) : deltas minuscules → gain plus fort ;
        // - facteur borné par événement (un cran de souris ±120 px ≈ ±15 %,
        //   comme avant — le trackpad, lui, devient progressif et précis).
        const px = e.deltaY * (e.deltaMode === 1 ? 16 : 1);
        const gain = e.ctrlKey ? 0.01 : 0.0022;
        const factor = Math.min(1.15, Math.max(0.87, Math.exp(-px * gain)));
        // Une échelle vide (onglet ouvert sans vue) donnait NaN : le zoom restait mort.
        const echelle = Number.isFinite(canvas.scale) && canvas.scale > 0 ? canvas.scale : 3;
        setCanvas({ scale: Math.min(Math.max(0.1, echelle * factor), 100) });
    };

    const handleMouseDown = (e) => {
        if (mainAuxCommandes) {
            setIsDragging(true);
            dragStartRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
            return;
        }
        // Désélection à l'APPUI sur le fond gris UNIQUEMENT (e.target = ce
        // conteneur lui-même — les mousedown sur les éléments sont stoppés par
        // handleDragStart et n'arrivent pas ici). On ne désélectionne JAMAIS au
        // `click` : si la cible change entre l'appui et le relâchement
        // (re-render du z-index à la sélection, micro-drag), le navigateur
        // délivre le click à l'ancêtre commun → ça désélectionnait
        // aléatoirement l'élément qu'on venait de sélectionner.
        if (e.button === 0 && e.target === e.currentTarget) selectElement(null);
    };

    const handleMouseMove = (e) => {
        if (!isDragging || !mainAuxCommandes) return;
        setPan({
            x: e.clientX - dragStartRef.current.x,
            y: e.clientY - dragStartRef.current.y
        });
    };

    const handleMouseUp = () => setIsDragging(false);
    const handleMouseLeave = () => setIsDragging(false);

    const handleRotateStart = (e, el) => {
        e.stopPropagation();
        e.preventDefault();

        const rect = document.getElementById(`rnd-${el.id}`).getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;

        const startAngle = Math.atan2(e.clientY - cy, e.clientX - cx) * 180 / Math.PI;
        const initialRotation = el.rotation || 0;

        const onMove = (moveEvent) => {
            const currentAngle = Math.atan2(moveEvent.clientY - cy, moveEvent.clientX - cx) * 180 / Math.PI;
            let delta = currentAngle - startAngle;
            let newRotation = (initialRotation + delta) % 360;
            if (moveEvent.shiftKey) {
                newRotation = Math.round(newRotation / 15) * 15;
            }
            if (newRotation < 0) newRotation += 360;
            updateElement(el.id, { rotation: Math.round(newRotation) });
        };

        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            setTimeout(() => {
                if (useLabelStore.getState()._pushState) {
                    useLabelStore.getState()._pushState('Rotation');
                }
            }, 0);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    // --- Redimensionnement d'un calque PIVOTÉ par ses angles ---
    // react-rnd calcule en coordonnées écran alignées sur les axes et ignore
    // les transforms : impossible de lui confier un calque tourné (les poignées
    // natives sont désactivées dans ce cas). Ici : géométrie complète — la
    // souris est projetée dans le repère de l'objet (rotation inverse), la
    // taille recalculée, et le COIN OPPOSÉ reste fixé à l'écran, comme dans
    // Illustrator/InDesign.
    const handleRotatedResizeStart = (e, el, corner) => {
        e.stopPropagation();
        e.preventDefault();
        const inner = document.getElementById(`rnd-${el.id}`);
        if (!inner) return;
        const r = inner.getBoundingClientRect();
        const cx0 = r.left + r.width / 2;   // centre écran (invariant par rotation)
        const cy0 = r.top + r.height / 2;
        const theta = ((el.rotation || 0) * Math.PI) / 180;
        const cos = Math.cos(theta), sin = Math.sin(theta);
        const scale = canvas.scale;
        const { x: x0, y: y0, width: w0, height: h0 } = el;
        const sx = corner.includes('l') ? -1 : 1;  // signe du coin saisi (repère local)
        const sy = corner.includes('t') ? -1 : 1;
        // Coin OPPOSÉ (fixe) à l'écran : centre + R(θ)·(−sx·w0/2, −sy·h0/2)·scale
        const fx = cx0 + ((-sx * w0 / 2) * cos - (-sy * h0 / 2) * sin) * scale;
        const fy = cy0 + ((-sx * w0 / 2) * sin + (-sy * h0 / 2) * cos) * scale;
        const freeRatio = el.type === 'shape' && el.shapeType === 'line';

        const onMove = (me) => {
            // Vecteur coin-fixe → souris, ramené dans le repère de l'objet R(−θ)
            const vx = me.clientX - fx;
            const vy = me.clientY - fy;
            const lx = vx * cos + vy * sin;
            const ly = -vx * sin + vy * cos;
            const minPx = 2 * scale; // taille minimale 2 mm
            let newW = Math.max(minPx, lx * sx);
            let newH = Math.max(minPx, ly * sy);
            // Ratio verrouillé par défaut (Shift = libre), comme les calques droits
            if (!me.shiftKey && !freeRatio) {
                const s = Math.max(newW / (w0 * scale), newH / (h0 * scale));
                newW = Math.max(minPx, w0 * scale * s);
                newH = Math.max(minPx, h0 * scale * s);
            }
            // Nouveau centre écran = coin fixe + R(θ)·(sx·newW/2, sy·newH/2)
            const ncx = fx + ((sx * newW / 2) * cos - (sy * newH / 2) * sin);
            const ncy = fy + ((sx * newW / 2) * sin + (sy * newH / 2) * cos);
            const wMm = newW / scale;
            const hMm = newH / scale;
            updateElement(el.id, {
                width: wMm,
                height: hMm,
                x: (x0 + w0 / 2) + (ncx - cx0) / scale - wMm / 2,
                y: (y0 + h0 / 2) + (ncy - cy0) / scale - hMm / 2,
            });
        };
        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            setTimeout(() => {
                if (useLabelStore.getState()._pushState) {
                    useLabelStore.getState()._pushState('Redimensionnement');
                }
            }, 0);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    // --- Création de repères depuis les règles ---
    const handleRulerMouseDown = (e, orientation) => {
        if (!canvas.showRulers || mainAuxCommandes) return;
        e.preventDefault();
        e.stopPropagation();

        const rect = document.getElementById("forge-canvas-container").getBoundingClientRect();
        let initX = (e.clientX - rect.left) / canvas.scale - bleed;
        let initY = (e.clientY - rect.top) / canvas.scale - bleed;

        const elId = crypto.randomUUID();
        useLabelStore.getState().addElement({
            id: elId,
            type: 'guide',
            guideType: orientation,
            x: orientation === 'vertical' ? initX : 0,
            y: orientation === 'horizontal' ? initY : 0,
            locked: false,
            name: `Repère ${orientation === 'horizontal' ? 'Horizontal' : 'Vertical'}`,
            width: 0,
            height: 0
        });

        useLabelStore.getState().selectElement(elId, false);

        const scale = canvas.scale;
        let startClientX = e.clientX;
        let startClientY = e.clientY;

        const onMove = (moveEvent) => {
            const rawDx = (moveEvent.clientX - startClientX) / scale;
            const rawDy = (moveEvent.clientY - startClientY) / scale;
            
            let intendedX = (orientation === 'vertical' ? initX : 0) + rawDx;
            let intendedY = (orientation === 'horizontal' ? initY : 0) + rawDy;

            const stateCanvas = useLabelStore.getState().canvas;
            if (stateCanvas.snapToGrid !== false && !moveEvent.altKey) {
                const gridSize = 10;
                const snapThreshold = 4 / scale;
                if (orientation === 'vertical') {
                    const snapX = Math.round(intendedX / gridSize) * gridSize;
                    if (Math.abs(intendedX - snapX) < snapThreshold) intendedX = snapX;
                } else {
                    const snapY = Math.round(intendedY / gridSize) * gridSize;
                    if (Math.abs(intendedY - snapY) < snapThreshold) intendedY = snapY;
                }
            }
            
            useLabelStore.setState((s) => ({
                elements: s.elements.map(v => {
                    if (v.id === elId) {
                        return { 
                            ...v, 
                            x: v.guideType === 'vertical' ? intendedX : v.x, 
                            y: v.guideType === 'horizontal' ? intendedY : v.y 
                        };
                    }
                    return v;
                })
            }));
        };

        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            setTimeout(() => {
                if (useLabelStore.getState()._pushState) {
                    useLabelStore.getState()._pushState('Nouveau Repère');
                }
            }, 0);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };
    // Sélection effective : une feuille est "sélectionnée" si elle l'est OU si un
    // de ses groupes ANCÊTRES l'est (cascade visuelle + déplacement de groupe).
    const ancestorGroupSelected = (it) => {
        let cur = it, guard = 0;
        while (cur && cur.parentId && guard++ < 1000) {
            if (selectedElementIds.includes(cur.parentId)) return true;
            cur = elements.find(x => x.id === cur.parentId);
        }
        return false;
    };
    // Verrou effectif : feuille verrouillée OU contenue dans un groupe verrouillé.
    const isLeafLocked = (it) => {
        if (it.locked) return true;
        let cur = it, guard = 0;
        while (cur && cur.parentId && guard++ < 1000) {
            const p = elements.find(x => x.id === cur.parentId);
            if (p && p.locked) return true;
            cur = p;
        }
        return false;
    };

    // Maj+clic est traité à DEUX endroits, et ils se contredisaient. L'appui
    // (handleDragStart) AJOUTE le calque à la sélection, pour qu'on puisse le
    // glisser aussitôt avec les autres ; puis le clic, qui suit le relâchement,
    // BASCULAIT le même calque — et retirait donc celui qu'on venait d'ajouter.
    // Selon l'endroit cliqué, le clic atteignait le calque ou non : la sélection
    // à trois éléments et plus devenait une loterie (retour de Louise,
    // 2026-10-01). L'appui note ici ce qu'il a trouvé, le clic s'en sert.
    const gesteMajRef = useRef({ id: null, etaitSelectionne: false, aBouge: false });

    const handleDragStart = (e, el) => {
        if (e.button !== 0) return;
        e.stopPropagation();
        // Verrou en cascade : feuille verrouillée ou dans un groupe verrouillé → pas de drag.
        if (isLeafLocked(el)) return;

        const state = useLabelStore.getState();
        const currentSelected = state.selectedElementIds;
        gesteMajRef.current = {
            id: el.id,
            etaitSelectionne: currentSelected.includes(el.id),
            aBouge: false,
        };

        // Si un groupe ANCÊTRE est déjà sélectionné, on le conserve (on déplacera
        // tout le groupe) ; sinon on sélectionne la feuille au mousedown.
        const ancSel = ancestorGroupSelected(el);
        if (!currentSelected.includes(el.id) && !ancSel) {
            state.selectElement(el.id, e.shiftKey);
        }

        // En mode Gomme ou Zone, un clic sur un calque le DÉSIGNE comme cible, il
        // ne le déplace pas. Sans cette sortie, changer de calque à gommer — ou
        // à découper — suffirait à décaler la composition.
        if (outilEffectif === 'eraser' || outilEffectif === 'marquee') return;

        const refreshedState = useLabelStore.getState();
        let idsToMove = new Set();
        const baseSel = (refreshedState.selectedElementIds.includes(el.id) || ancSel)
            ? refreshedState.selectedElementIds : [el.id];
        // Expansion groupe → feuilles descendantes (les groupes n'ont pas de géométrie propre).
        for (const sid of baseSel) {
            ajouterFeuilles(refreshedState.elements, sid, idsToMove);
        }
        if (el.linkedId) {
            refreshedState.elements
                .filter(v => v.linkedId === el.linkedId)
                .forEach(v => ajouterFeuilles(refreshedState.elements, v.id, idsToMove));
        }

        const scale = refreshedState.canvas.scale;
        let startClientX = e.clientX;
        let startClientY = e.clientY;
        
        const initialElements = refreshedState.elements.map(v => ({...v}));
        const primaryEl = initialElements.find(v => v.id === el.id);
        let moved = false;

        const onMove = (moveEvent) => {
            const rawDx = (moveEvent.clientX - startClientX) / scale;
            const rawDy = (moveEvent.clientY - startClientY) / scale;

            let intendedX = primaryEl.x + rawDx;
            let intendedY = primaryEl.y + rawDy;

            const stateCanvas = useLabelStore.getState().canvas;

            let lockedDx = rawDx;
            let lockedDy = rawDy;
            let snappedX = false;
            let snappedY = false;
            // Coordonnées absolues (mm dans le système canvas) sur lesquelles on
            // snappe. null si pas de snap actif sur cet axe. Utilisé en fin de
            // tick pour afficher les lignes guides bleues.
            let snapXLine = null;
            let snapYLine = null;
            const snapThreshold = SEUIL_MAGNETISME_PX / scale;

            if (!moveEvent.altKey) {
                const allElements = useLabelStore.getState().elements;
                const stateCanvasData = useLabelStore.getState().canvas;

                // Snap Repères
                if (stateCanvas.snapToGuides !== false) {
                    const horizontalGuides = allElements.filter(g => g.type === 'guide' && g.guideType === 'horizontal').map(g => g.y);
                    const verticalGuides = allElements.filter(g => g.type === 'guide' && g.guideType === 'vertical').map(g => g.x);

                    // Le CADRE de l'étiquette — bords, centre, marges de sécurité — est
                    // une cible de magnétisme au même titre qu'un repère posé à la main.
                    // Il ne l'était pas : seuls les deux axes médians l'étaient, et
                    // encore, à la condition que les repères soient AFFICHÉS. Coller un
                    // calque au bord du format, ou le poser sur la marge de sécurité,
                    // était donc un geste à la main — d'où « difficulté à positionner
                    // une forme précisément sur les repères » au retour de septembre.
                    // Le cadre existe indépendamment de l'affichage des repères : c'est
                    // le document lui-même, pas une aide qu'on montre ou qu'on cache.
                    verticalGuides.push(...ciblesDuCadre(stateCanvasData.width_mm, stateCanvasData.margin_mm));
                    horizontalGuides.push(...ciblesDuCadre(stateCanvasData.height_mm, stateCanvasData.margin_mm));

                    const ax = accrocher(intendedX, primaryEl.width, verticalGuides, snapThreshold);
                    if (ax.ligne !== null) { intendedX = ax.position; snappedX = true; snapXLine = ax.ligne; }

                    const ay = accrocher(intendedY, primaryEl.height, horizontalGuides, snapThreshold);
                    if (ay.ligne !== null) { intendedY = ay.position; snappedY = true; snapYLine = ay.ligne; }
                }

                // Snap Intelligent — Alignement avec les AUTRES éléments
                if (!snappedX || !snappedY) {
                    const otherElements = allElements.filter(el =>
                        el.type !== 'guide' && !el.hidden && !idsToMove.has(el.id)
                    );
                    // Mêmes trois points d'accroche que pour les repères, sur les
                    // bords et le milieu de chaque autre calque.
                    if (!snappedX) {
                        const cibles = otherElements.flatMap(o => [o.x, o.x + o.width, o.x + o.width / 2]);
                        const a = accrocher(intendedX, primaryEl.width, cibles, snapThreshold);
                        if (a.ligne !== null) { intendedX = a.position; snappedX = true; snapXLine = a.ligne; }
                    }

                    if (!snappedY) {
                        const cibles = otherElements.flatMap(o => [o.y, o.y + o.height, o.y + o.height / 2]);
                        const a = accrocher(intendedY, primaryEl.height, cibles, snapThreshold);
                        if (a.ligne !== null) { intendedY = a.position; snappedY = true; snapYLine = a.ligne; }
                    }
                }

                // Snap Grille (10mm Default)
                if (stateCanvas.snapToGrid !== false) {
                    const gridSize = 10;
                    if (!snappedX) {
                        const snapX = Math.round(intendedX / gridSize) * gridSize;
                        if (Math.abs(intendedX - snapX) < snapThreshold) intendedX = snapX;
                    }
                    if (!snappedY) {
                        const snapY = Math.round(intendedY / gridSize) * gridSize;
                        if (Math.abs(intendedY - snapY) < snapThreshold) intendedY = snapY;
                    }
                }
            }

            lockedDx = intendedX - primaryEl.x;
            lockedDy = intendedY - primaryEl.y;

            // Mise à jour des lignes guides à chaque tick — l'overlay SVG les
            // affiche en bleu pendant tout le snap, disparaissent dès qu'on
            // décroche (snapXLine/Yline === null).
            setSmartGuides({
                vertical:   snapXLine != null ? [snapXLine] : [],
                horizontal: snapYLine != null ? [snapYLine] : [],
            });

            if (Math.abs(lockedDx) < 0.01 && Math.abs(lockedDy) < 0.01) return;
            moved = true;

            useLabelStore.setState((s) => ({
                elements: s.elements.map(v => {
                    if (idsToMove.has(v.id)) {
                        const original = initialElements.find(o => o.id === v.id);
                        return { ...v, x: original.x + lockedDx, y: original.y + lockedDy };
                    }
                    return v;
                })
            }));
        };

        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            // Le clic qui suit ce relâchement doit savoir qu'il conclut un glissement.
            gesteMajRef.current.aBouge = moved;
            // Clear les smart guides quand on relâche
            setSmartGuides({ vertical: [], horizontal: [] });
            if (moved) {
                setTimeout(() => {
                    if (useLabelStore.getState()._pushState) {
                        useLabelStore.getState()._pushState('Déplacement');
                    }
                }, 0);
            }
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };





    // Dimensions de base de l'étiquette (échelle 1:1)
    const baseWidth = canvas.width_mm;
    const baseHeight = canvas.height_mm;
    const bleed = canvas.bleed_mm || 0;

    const renderRulerTicks = (dimensionStr) => {
        const dimension = Number(dimensionStr) || 100;
        const ticks = [];
        const s = canvas.scale;

        // Adaptive steps based on zoom — ensure labels never overlap
        // The key metric: pixels between major labels must be >= 30px
        let step, minorStep;

        if (s >= 15)      { step = 1;   minorStep = 0.25; }  // Very close: every mm, quarter subdivisions
        else if (s >= 8)  { step = 2;   minorStep = 0.5;  }  // Close: every 2mm
        else if (s >= 4)  { step = 5;   minorStep = 1;    }  // Medium: every 5mm
        else if (s >= 2)  { step = 10;  minorStep = 2;    }  // Default: every 10mm (cm)
        else if (s >= 1)  { step = 20;  minorStep = 5;    }  // Zoom out: every 20mm
        else if (s >= 0.5){ step = 50;  minorStep = 10;   }  // Far: every 50mm
        else              { step = 100; minorStep = 20;   }  // Very far: every 100mm

        for (let i = 0; i <= dimension; i += minorStep) {
            const isMajor = Math.abs(i % step) < 0.01;
            const val = Number(i.toFixed(2));

            ticks.push({
                val: val,
                isMajor: isMajor,
                posPx: val * s
            });
        }
        return ticks;
    };

    // Dispatcher de rendu par type de calque. Le détail de chaque type est
    // dans son composant dédié (voir canvas/*Layer.jsx).
    const renderElementContent = (el) => {
        const backgroundStyle = el.fillType === 'gradient' ? getCssGradient(el.gradient) : 'none';

        // text-shadow CSS pré-calculé pour les calques texte (text-shadow ne
        // supporte pas spread, on utilise blur = size - spread → quand spread
        // est élevé l'ombre est plus dense, comme dans Photoshop).
        // AVANT : la condition portait aussi `!textShadowParams.inset`, or
        // text-shadow n'a PAS de variante interne : passer un calque texte en
        // « Interne » ne fabriquait aucune ombre interne et supprimait en plus
        // l'ombre externe qu'on avait avant de cliquer. Le drapeau est donc
        // ignoré pour le texte : l'ombre externe reste rendue.
        const textShadowParams = getShadowParams(el);
        let textShadowCSS = 'none';
        if (textShadowParams && el.type === 'text') {
            const p = textShadowParams;
            textShadowCSS = `${p.offsetX}px ${p.offsetY}px ${p.cssBlur}px ${p.rgba}`;
        }

        switch (el.type) {
            case 'text':
                return (
                    <TextLayer
                        el={el}
                        canvas={canvas}
                        editingId={editingId}
                        setEditingId={setEditingId}
                        selectElement={selectElement}
                        updateElement={updateElement}
                        textContainerRefs={textContainerRefs}
                        textShadowCSS={textShadowCSS}
                        backgroundStyle={backgroundStyle}
                    />
                );
            case 'barcode':
                return <BarcodeElement el={el} />;
            case 'image':
                // Gomme active sur CE calque : la surface de peinture se
                // superpose à l'image et prend la main sur les événements
                // pointeur (l'image reste affichée dessous, masque compris,
                // pour que le rendu ne saute pas entre deux traits).
                return (
                    <>
                        <ImageLayer el={el} />
                        {outilEffectif === 'eraser' && selectedElementIds.includes(el.id) && !isLeafLocked(el) && (
                            <EraserSurface el={el} scale={canvas.scale} />
                        )}
                        {outilEffectif === 'marquee' && selectedElementIds.includes(el.id) && !isLeafLocked(el) && (
                            <MarqueeSurface el={el} scale={canvas.scale} />
                        )}
                    </>
                );
            case 'shape':
                return <ShapeLayer el={el} />;
            default:
                return <div>Inconnu</div>;
        }
    };

    return (
        <div
            className="w-full min-h-full overflow-hidden bg-black/20 relative select-none"
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            style={{ cursor: mainAuxCommandes ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
        >

            {/* SPACEBAR OVERLAY (Bloque les clics des calques pour un Drag garanti).
                z-50 = plafond de l'échelle "UI locale" : contexte d'empilement
                local au canvas, doit juste passer au-dessus des calques/guides. */}
            {mainAuxCommandes && (
                <div className="absolute inset-0 z-50" />
            )}

            {/* RÈGLES FIXES (Type Photoshop - Restent toujours affichées aux bords) */}
            {canvas.showRulers && (
                <>
                    {/* Règle Top */}
                    <div 
                        className="absolute top-0 left-0 right-0 h-[20px] z-[80] bg-forge-bg/95 border-b border-white/10 overflow-hidden cursor-row-resize"
                        onMouseDown={(e) => handleRulerMouseDown(e, 'horizontal')}
                    >
                        <div 
                            style={{ 
                                position: 'absolute',
                                left: '50%',
                                top: 0,
                                bottom: 0,
                                width: baseWidth * canvas.scale, 
                                transform: `translateX(calc(-50% + ${pan.x}px))` 
                            }}
                        >
                            {renderRulerTicks(canvas.width_mm).map(t => (
                                <div key={`t-${t.val}`} className="absolute bottom-0 border-l border-white/10 text-white" style={{ left: `${t.posPx}px`, height: t.isMajor ? '100%' : '30%' }}>
                                    {t.isMajor && <span className="absolute top-0 left-[2px] text-[8px] font-mono leading-none">{t.val}</span>}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Règle Left */}
                    <div 
                        className="absolute top-0 bottom-0 left-0 w-[20px] z-[80] bg-forge-bg/95 border-r border-white/10 overflow-hidden flex flex-col items-end cursor-col-resize"
                        onMouseDown={(e) => handleRulerMouseDown(e, 'vertical')}
                    >
                        <div 
                            style={{ 
                                position: 'absolute',
                                top: '50%',
                                left: 0,
                                right: 0,
                                height: baseHeight * canvas.scale, 
                                transform: `translateY(calc(-50% + ${pan.y}px))` 
                            }}
                        >
                            {renderRulerTicks(canvas.height_mm).map(t => (
                                <div key={`l-${t.val}`} className="absolute right-0 border-t border-white/10 text-white" style={{ top: `${t.posPx}px`, width: t.isMajor ? '100%' : '30%' }}>
                                    {t.isMajor && <span className="absolute left-0 top-[1px] text-[8px] font-mono leading-none w-full text-center" style={{ transform: 'rotate(-90deg) translateX(-50%)', transformOrigin: 'top left', marginTop: '4px' }}>{t.val}</span>}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Coin d'intersection en haut à gauche pour masquer les chevauchements */}
                    <div className="absolute top-0 left-0 w-[20px] h-[20px] z-[85] bg-forge-bg border-r border-b border-white/10 flex items-center justify-center">
                        <span className="text-[7px] text-white/40 font-mono font-bold select-none opacity-50">mm</span>
                    </div>
                </>
            )}

            {/* D-pad de déplacement de la vue + bouton central reset zoom 1:1.
                Le maintien d'un bouton de direction enclenche un pan continu
                (setInterval) tant que la souris reste appuyée. Le bouton central
                réinitialise le zoom à 100% et recentre la vue. */}
            <ViewDPad setPan={setPan} setCanvas={setCanvas} />

            {/* Wrapper Canvas absolu au centre mathématique du Viewport pour sync parfaite */}
            <div
                style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: baseWidth * canvas.scale,
                    height: baseHeight * canvas.scale,
                    transform: `translate(calc(-50% + ${pan.x}px), calc(-50% + ${pan.y}px))`
                }}
                className="z-10"
                // Désélection à l'APPUI (pas au click — cf. handleMouseDown) quand
                // l'appui vise le fond : ce wrapper lui-même ou le fond de
                // l'étiquette. Les mousedown sur les éléments sont stoppés par
                // handleDragStart ; ceux sur les poignées de resize (enfants Rnd)
                // ne matchent pas ces cibles → un resize ne désélectionne pas.
                onMouseDown={(e) => {
                    const t = e.target;
                    if (e.button === 0 && (t === e.currentTarget || t.id === 'forge-canvas-container')) {
                        e.stopPropagation();
                        selectElement(null);
                    }
                }}
            >

                {/* Le Canvas proprement dit mis à l'échelle via CSS */}
                <div
                    id="forge-canvas-container"
                    data-export-canvas="true"
                    className="absolute shadow-2xl origin-top-left flex-shrink-0"
                    style={{
                        top: 0,
                        left: 0,
                        width: baseWidth + bleed * 2,
                        height: baseHeight + bleed * 2,
                        marginLeft: -bleed * canvas.scale,
                        marginTop: -bleed * canvas.scale,
                        backgroundColor: canvas.color_bg,
                        transform: `scale(${canvas.scale})`,
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
                    }}
                    onDragOver={(e) => {
                        // Si l'utilisateur drag une image depuis le Finder/Bureau,
                        // accepter le drop sur le canvas (la barre d'outils ne reçoit
                        // pas le drop puisque le contenu draggé n'est pas un calque).
                        if (e.dataTransfer.types.includes('Files')) {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'copy';
                        }
                    }}
                    onDrop={(e) => {
                        const files = Array.from(e.dataTransfer.files || []).filter(f => f.type.startsWith('image/'));
                        if (files.length === 0) return;
                        e.preventDefault();
                        e.stopPropagation();
                        // Coordonnées du drop en mm. e.currentTarget = ce wrapper qui
                        // a déjà subi le transform: scale(canvas.scale), donc on n'a
                        // PAS à diviser par scale ici — getBoundingClientRect retourne
                        // les dimensions à l'écran qui sont déjà scaled.
                        const rect = e.currentTarget.getBoundingClientRect();
                        const dropX_mm = ((e.clientX - rect.left) / canvas.scale) - bleed;
                        const dropY_mm = ((e.clientY - rect.top) / canvas.scale) - bleed;
                        const { addElement } = useLabelStore.getState();
                        files.forEach((file) => {
                            const reader = new FileReader();
                            reader.onload = (ev) => {
                                const dataUrl = ev.target.result;
                                const img = new Image();
                                img.onload = () => {
                                    // Largeur cible 50 mm (ou plus petit si proche d'un bord),
                                    // hauteur ajustée au ratio. Position : centrée sur le drop.
                                    const ratio = img.width / img.height;
                                    const targetW = Math.min(50, baseWidth * 0.6);
                                    const targetH = targetW / ratio;
                                    addElement({
                                        type: 'image',
                                        url: dataUrl,
                                        isAI: false,
                                        x: dropX_mm - targetW / 2,
                                        y: dropY_mm - targetH / 2,
                                        width: targetW,
                                        height: targetH,
                                        rotation: 0,
                                    });
                                    // Un dépôt d'image doit s'annuler comme une duplication.
                                    setTimeout(() => useLabelStore.getState()._pushState('Ajout : image déposée'), 0);
                                };
                                img.src = dataUrl;
                                // Fire-and-forget : on sauvegarde l'image dans la galerie
                                // en parallèle de l'ajout au canvas, pour que l'utilisateur
                                // retrouve toutes ses images uploadées au même endroit
                                // (workflow_type 'import' pour les distinguer des t2i/express).
                                // Non-bloquant : si la sauvegarde échoue (réseau, quota), le
                                // calque reste sur le canvas.
                                saveToGallery({
                                    imageB64: dataUrl,
                                    filename: file.name || `import_${Date.now()}.png`,
                                    originalPrompt: file.name || '',
                                    workflowType: 'import',
                                    metadataJson: {
                                        from: 'canvas_drop',
                                        original_filename: file.name || null,
                                        mime: file.type || null,
                                        size_bytes: file.size || null,
                                    },
                                    folderId: null,
                                }).catch((e) => {
                                    console.warn('Auto-save dropped image to gallery failed (non-blocking):', e);
                                });
                            };
                            reader.readAsDataURL(file);
                        });
                    }}
                >
                    {/* Grille magnétique */}
                    {canvas.showGrid && (
                        <div data-no-export="true" className="absolute inset-0 pointer-events-none z-0" style={{
                            backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)`,
                            backgroundSize: '10px 10px',
                            backgroundPosition: `${bleed}px ${bleed}px`
                        }} />
                    )}

                    {/* Lignes de coupe et de marge via SVG. data-no-export pour
                        que html2canvas ignore ce SVG pendant l'export PDF/PNG :
                        ces traits (rect rouge du fond perdu, rect bleu pointille
                        de la marge interieure) sont des reperes visuels qui ne
                        doivent jamais apparaitre sur le PDF imprime. */}
                    <svg data-no-export="true" className="absolute top-0 left-0 w-full h-full pointer-events-none z-[48]" style={{ overflow: 'visible' }}>
                        {(canvas.showBleed !== false) && bleed > 0 && (
                            <rect
                                x={bleed} y={bleed}
                                width={baseWidth} height={baseHeight}
                                fill="none"
                                stroke="rgba(239, 68, 68, 0.6)"
                                strokeWidth={1 / canvas.scale}
                            />
                        )}
                        {(canvas.showMargin !== false) && canvas.margin_mm > 0 && (
                            <rect
                                x={bleed + canvas.margin_mm} y={bleed + canvas.margin_mm}
                                width={baseWidth - 2 * canvas.margin_mm} height={baseHeight - 2 * canvas.margin_mm}
                                fill="none"
                                stroke="rgba(59, 130, 246, 0.6)"
                                strokeWidth={1 / canvas.scale}
                                strokeDasharray={`${4 / canvas.scale}, ${4 / canvas.scale}`}
                            />
                        )}
                    </svg>

                    {/* Repères Centraux — data-no-export pour ne pas apparaitre dans le PDF */}
                    {canvas.showGuides && (
                        <>
                            <div data-no-export="true" className="absolute left-0 right-0 bg-cyan-500/80 pointer-events-none z-[49]" style={{ top: bleed + baseHeight / 2, height: `${1 / canvas.scale}px` }} />
                            <div data-no-export="true" className="absolute top-0 bottom-0 bg-cyan-500/80 pointer-events-none z-[49]" style={{ left: bleed + baseWidth / 2, width: `${1 / canvas.scale}px` }} />
                        </>
                    )}

                    {/* Smart guides — lignes pointillées bleues affichées en temps réel
                        pendant un drag quand le calque s'aligne avec un autre calque /
                        guide / centre. Pas dans l'export (data-no-export). Style Figma. */}
                    {(smartGuides.vertical.length > 0 || smartGuides.horizontal.length > 0) && (
                        <svg data-no-export="true" className="absolute top-0 left-0 w-full h-full pointer-events-none z-[55]" style={{ overflow: 'visible' }}>
                            {smartGuides.vertical.map((x, i) => (
                                <line
                                    key={`smart-v-${i}`}
                                    x1={bleed + x} y1={-20}
                                    x2={bleed + x} y2={baseHeight + 40}
                                    stroke="rgba(99, 102, 241, 0.95)"
                                    strokeWidth={1 / canvas.scale}
                                    strokeDasharray={`${4 / canvas.scale} ${3 / canvas.scale}`}
                                />
                            ))}
                            {smartGuides.horizontal.map((y, i) => (
                                <line
                                    key={`smart-h-${i}`}
                                    x1={-20} y1={bleed + y}
                                    x2={baseWidth + 40} y2={bleed + y}
                                    stroke="rgba(99, 102, 241, 0.95)"
                                    strokeWidth={1 / canvas.scale}
                                    strokeDasharray={`${4 / canvas.scale} ${3 / canvas.scale}`}
                                />
                            ))}
                        </svg>
                    )}

                    {/* 1. L'Image IA Centrale (Fond Pur, sans Rnd) */}
                    {aiImage && aiImage.url && (
                        <img
                            src={aiImage.url}
                            alt="AI Background"
                            className="absolute top-0 left-0 w-full h-full object-cover z-0 pointer-events-none opacity-90"
                        />
                    )}

                    {/* 2. Les Éléments — feuilles aplaties dans l'ordre de peinture.
                        Les groupes (type:'group') n'ont pas de pixels propres → exclus.
                        Masquage hérité en cascade depuis les groupes parents (effHidden). */}
                    {orderedLeaves(elements).map(({ el, effHidden }, index) => {
                        if (effHidden) return null;
                        const isSelected = selectedElementIds.includes(el.id) || ancestorGroupSelected(el);

                        if (el.type === 'guide') {
                            return (
                                <div
                                    key={el.id}
                                    onMouseDown={(e) => {
                                        if (el.locked || e.button !== 0) return;
                                        e.stopPropagation();
                                        selectElement(el.id, e.shiftKey);
                                        const stateObj = useLabelStore.getState();
                                        const originalElement = stateObj.elements.find(v => v.id === el.id);
                                        const scale = canvas.scale;
                                        let startClientX = e.clientX;
                                        let startClientY = e.clientY;

                                        const onMove = (moveEvent) => {
                                            const rawDx = (moveEvent.clientX - startClientX) / scale;
                                            const rawDy = (moveEvent.clientY - startClientY) / scale;
                                            
                                            let intendedX = originalElement.x + rawDx;
                                            let intendedY = originalElement.y + rawDy;

                                            const stateCanvas = useLabelStore.getState().canvas;
                                            if (stateCanvas.snapToGrid !== false && !moveEvent.altKey) {
                                                const gridSize = 10;
                                                const snapThreshold = 4 / scale;
                                                if (el.guideType === 'vertical') {
                                                    const snapX = Math.round(intendedX / gridSize) * gridSize;
                                                    if (Math.abs(intendedX - snapX) < snapThreshold) intendedX = snapX;
                                                } else {
                                                    const snapY = Math.round(intendedY / gridSize) * gridSize;
                                                    if (Math.abs(intendedY - snapY) < snapThreshold) intendedY = snapY;
                                                }
                                            }

                                            useLabelStore.setState((s) => ({
                                                elements: s.elements.map(v => v.id === el.id ? { ...v, x: v.guideType === 'vertical' ? intendedX : v.x, y: v.guideType === 'horizontal' ? intendedY : v.y } : v)
                                            }));
                                        };
                                        const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
                                        window.addEventListener('mousemove', onMove);
                                        window.addEventListener('mouseup', onUp);
                                    }}
                                    className={`absolute z-[120] group ${el.locked ? '' : (el.guideType === 'horizontal' ? 'cursor-row-resize' : 'cursor-col-resize')}`}
                                    style={{
                                        ...(el.guideType === 'horizontal' ? {
                                            left: -10000, right: -10000, 
                                            top: el.y + bleed, 
                                            height: 12 / canvas.scale, 
                                            transform: 'translateY(-50%)'
                                        } : {
                                            top: -10000, bottom: -10000, 
                                            left: el.x + bleed, 
                                            width: 12 / canvas.scale, 
                                            transform: 'translateX(-50%)'
                                        })
                                    }}
                                >
                                    <svg 
                                        width="100%" height="100%" 
                                        className="pointer-events-none overflow-visible absolute inset-0" 
                                    >
                                        {el.guideType === 'horizontal' ? (
                                            <line 
                                                x1="0" y1="50%" x2="100%" y2="50%" 
                                                stroke={isSelected ? '#3b82f6' : '#22d3ee'} 
                                                strokeWidth={1 / canvas.scale} 
                                            />
                                        ) : (
                                            <line 
                                                x1="50%" y1="0" x2="50%" y2="100%" 
                                                stroke={isSelected ? '#3b82f6' : '#22d3ee'} 
                                                strokeWidth={1 / canvas.scale} 
                                            />
                                        )}
                                    </svg>
                                </div>
                            );
                        }

                        // Style visuel pour l'élément sélectionné
                        const outlineThickness = 0.2 / canvas.scale;
                        const handleSize = 6 / canvas.scale;

                        // Hitboxes des handles. Toutes les valeurs sont en unités locales
                        // (divisées par canvas.scale) pour rester constantes à l'écran.
                        // - Coins : carrés centrés sur le coin, 14×14 px à l'écran
                        // - Bords : bandes sur TOUTE la longueur du bord
                        //   (sauf l'emprise des coins, pour ne pas masquer leur curseur)
                        //
                        // MAIS ces 14 px sont un PLAFOND, pas une constante. Un élément petit à
                        // l'écran ne peut pas porter quatre coins de 14 px : ils en occupent 28,
                        // et la bande de bord — calc(100% - 28px) — tombait à zéro dès que
                        // l'élément descendait sous ~28 px. Les coins opposés se recouvraient,
                        // plus aucune poignée n'était attrapable, et le calque devenait
                        // IMPOSSIBLE À RAPETISSER À LA SOURIS : il fallait passer par
                        // l'inspecteur. C'est exactement la taille d'un pictogramme
                        // réglementaire — 5 à 10 mm, soit 19 à 38 px à zoom 100 % — donc le cas
                        // le plus courant du métier tombait pile dans le trou. Signalé à l'essai
                        // atelier de septembre : « blocage pour rapetisser une forme au-delà
                        // d'un certain point sans repasser par les réglages latéraux ».
                        //
                        // Les hitboxes rétrécissent donc AVEC l'élément, en gardant toujours un
                        // tiers de chaque bord libre entre les coins. Au-dessus de ~42 px à
                        // l'écran, rien ne change : on retrouve les 14 px d'origine.
                        const largeurEcran = Math.max(1, el.width * canvas.scale);
                        const hauteurEcran = Math.max(1, el.height * canvas.scale);
                        const petitCote = Math.min(largeurEcran, hauteurEcran);
                        // Plancher à 4 px : sous cette taille une poignée n'est plus visable à la
                        // souris, et mieux vaut un léger recouvrement que plus rien du tout.
                        const cornerHit = Math.max(4, Math.min(14, petitCote / 3)) / canvas.scale;
                        // Épaisseur des bandes : bornée par la dimension qu'elles traversent, pour
                        // que les bandes opposées ne se recouvrent pas sur un calque fin.
                        const sideThickH = Math.max(4, Math.min(14, hauteurEcran / 2)) / canvas.scale; // haut / bas
                        const sideThickV = Math.max(4, Math.min(14, largeurEcran / 2)) / canvas.scale; // gauche / droite
                        const sideInset = `${cornerHit}px`; // marge gauche/haut pour ne pas chevaucher les coins
                        const sideLength = `calc(100% - ${cornerHit * 2}px)`;

                        const handleBase = {
                            opacity: 0, // INVISIBLE : SVG dessous comme rendu visuel
                            pointerEvents: 'auto',
                        };

                        // En édition inline : on monte le z-index pour passer au-dessus
                        // des overlays de sélection, et on désactive resize/handles.
                        const isEditingThis = el.id === editingId;
                        const handleDisplay = (isSelected && !isEditingThis) ? 'block' : 'none';

                        return (
                            <Rnd
                                key={el.id}
                                style={{
                                    // L'ORDRE DE PEINTURE VIENT DE L'ORDRE DU DOM, PAS D'UN z-index.
                                    //
                                    // Avant : `index + 1` pour l'ordre normal, `elements.length + 10`
                                    // pour le calque sélectionné, `+ 100` pendant une édition de texte.
                                    // Trois valeurs NON BORNÉES, alors que les repères sont à 120 et les
                                    // traits de coupe, la marge et les repères centraux entre 48 et 55.
                                    // Passé ~120 calques — le lot ordinaire d'un PDF importé éclaté — un
                                    // calque image franchissait le seuil et recouvrait TOUS les repères,
                                    // que l'utilisateur les ait placés au-dessus ou non (mesuré au banc :
                                    // « IMAGE DEVANT » dès 122 calques, sélectionné ou pas).
                                    //
                                    // Or `index + 1` ne faisait que reproduire l'ordre du DOM, que les
                                    // éléments positionnés respectent déjà sans z-index. On ne garde donc
                                    // qu'un rehaussement BORNÉ à deux crans, hors d'atteinte des repères :
                                    // 2 = calque sélectionné (ses poignées ne doivent pas passer sous un
                                    // calque supérieur), 3 = texte en cours d'édition.
                                    //
                                    // Depuis le 2026-09-17, le calque SÉLECTIONNÉ ne monte plus : un fond
                                    // sélectionné passait visuellement devant tout ce qui le recouvre,
                                    // ce qui trompait sur l'ordre réel des calques. Il reste à sa place
                                    // dans la pile ; seul son cadre jaune est redessiné par-dessus, dans
                                    // l'overlay de sélection (cf. après la boucle). Les poignées, elles,
                                    // restent dans le calque : là où un calque supérieur le recouvre, on
                                    // redimensionne par l'inspecteur, comme dans Illustrator.
                                    zIndex: isEditingThis ? 3 : undefined,
                                    // Un calque VERROUILLÉ laisse passer les clics (façon
                                    // Illustrator) : sinon un élément technique à grande
                                    // emprise (fond fidèle, trait de découpe) intercepte
                                    // tous les clics et rend le reste insélectionnable au
                                    // canvas. Il reste sélectionnable via le panneau Calques.
                                    pointerEvents: isLeafLocked(el) ? 'none' : undefined,
                                }}
                                size={{ width: el.width, height: el.height }}
                                position={{ x: el.x + bleed, y: el.y + bleed }}
                                scale={canvas.scale}
                                lockAspectRatio={!isResizingSide && !isShiftPressed && !(el.type === 'shape' && el.shapeType === 'line')}
                                disableDragging={true}
                                enableResizing={(isLeafLocked(el) || isEditingThis || outilEffectif === 'eraser' || outilEffectif === 'marquee' || (el.rotation && el.rotation !== 0)) ? false : {
                                    top: true, right: true, bottom: true, left: true,
                                    topRight: true, bottomRight: true, bottomLeft: true, topLeft: true
                                }}
                                resizeHandleStyles={{
                                    // Bandes latérales sur TOUTE la longueur du bord (hors emprise des coins)
                                    top:    { ...handleBase, top:    `${-sideThickH/2}px`, left:   sideInset, width: sideLength, height: `${sideThickH}px`, display: handleDisplay, cursor: 'ns-resize' },
                                    right:  { ...handleBase, right:  `${-sideThickV/2}px`, top:    sideInset, height: sideLength, width: `${sideThickV}px`, display: handleDisplay, cursor: 'ew-resize' },
                                    bottom: { ...handleBase, bottom: `${-sideThickH/2}px`, left:   sideInset, width: sideLength, height: `${sideThickH}px`, display: handleDisplay, cursor: 'ns-resize' },
                                    left:   { ...handleBase, left:   `${-sideThickV/2}px`, top:    sideInset, height: sideLength, width: `${sideThickV}px`, display: handleDisplay, cursor: 'ew-resize' },
                                    // Coins carrés 14×14 px centrés sur l'angle, avec curseurs diagonaux explicites
                                    topLeft:     { ...handleBase, top:    `${-cornerHit/2}px`, left:   `${-cornerHit/2}px`, width: `${cornerHit}px`, height: `${cornerHit}px`, display: handleDisplay, cursor: 'nwse-resize' },
                                    topRight:    { ...handleBase, top:    `${-cornerHit/2}px`, right:  `${-cornerHit/2}px`, width: `${cornerHit}px`, height: `${cornerHit}px`, display: handleDisplay, cursor: 'nesw-resize' },
                                    bottomLeft:  { ...handleBase, bottom: `${-cornerHit/2}px`, left:   `${-cornerHit/2}px`, width: `${cornerHit}px`, height: `${cornerHit}px`, display: handleDisplay, cursor: 'nesw-resize' },
                                    bottomRight: { ...handleBase, bottom: `${-cornerHit/2}px`, right:  `${-cornerHit/2}px`, width: `${cornerHit}px`, height: `${cornerHit}px`, display: handleDisplay, cursor: 'nwse-resize' },
                                }}
                                onResizeStart={(e, direction) => {
                                    const dir = direction.toLowerCase();
                                    const isSide = dir === 'top' || dir === 'right' || dir === 'bottom' || dir === 'left';
                                    setIsResizingSide(isSide);
                                }}
                                onResizeStop={(e, direction, ref, delta, position) => {
                                    setIsResizingSide(false);
                                    const dir = direction.toLowerCase();

                                    // Avant, `parseInt` TRONQUAIT les deux dimensions au millimètre
                                    // entier : tirer le bord droit d'un calque de 40,6 mm de haut lui
                                    // ramenait la hauteur à 40 mm sans que rien ne soit demandé, et le
                                    // reliquat perdu faisait dériver le bord opposé — censé rester
                                    // fixe — jusqu'à 1 mm sur un tirage par la gauche ou par le haut.
                                    // Désormais : arrondi au dixième (l'unité de toute l'application,
                                    // cf. le déplacement clavier), la dimension que le geste n'a pas
                                    // touchée n'est pas réécrite, et le bord opposé se déduit de la
                                    // taille RETENUE pour rester où il était.
                                    const auDixieme = (v) => Math.round(v * 10) / 10;
                                    const largeurChangee = dir.includes('left') || dir.includes('right') || delta.width !== 0;
                                    const hauteurChangee = dir.includes('top') || dir.includes('bottom') || delta.height !== 0;
                                    const newW = largeurChangee ? Math.max(0.1, auDixieme(parseFloat(ref.style.width))) : el.width;
                                    const newH = hauteurChangee ? Math.max(0.1, auDixieme(parseFloat(ref.style.height))) : el.height;

                                    // Utiliser NOTRE position car le position de react-rnd est faussé
                                    // quand disableDragging est à true.
                                    let newX = el.x;
                                    let newY = el.y;

                                    if (dir.includes('left')) newX = auDixieme(el.x + el.width - newW);
                                    if (dir.includes('top')) newY = auDixieme(el.y + el.height - newH);

                                    const dx = newX - el.x;
                                    const dy = newY - el.y;

                                    // Même règle que handleDragStart : la sélection comme les liens
                                    // peuvent porter un nœud de groupe, qu'il faut détendre en
                                    // feuilles (cf. ajouterFeuilles) sous peine de `x: NaN`.
                                    let idsToMove = new Set();
                                    const baseSel = isSelected ? selectedElementIds : [el.id];
                                    for (const sid of baseSel) {
                                        ajouterFeuilles(elements, sid, idsToMove);
                                    }
                                    if (el.linkedId) {
                                        elements
                                            .filter(e => e.linkedId === el.linkedId)
                                            .forEach(e => ajouterFeuilles(elements, e.id, idsToMove));
                                    }
                                    const elementsToUpdate = Array.from(idsToMove);

                                    useLabelStore.setState((state) => ({
                                        elements: state.elements.map(e => {
                                            if (e.id === el.id) {
                                                return { ...e, width: newW, height: newH, x: newX, y: newY };
                                            } else if (elementsToUpdate.includes(e.id)) {
                                                return { ...e, x: e.x + dx, y: e.y + dy };
                                            }
                                            return e;
                                        })
                                    }));
                                    setTimeout(() => {
                                        if (useLabelStore.getState()._pushState) {
                                            useLabelStore.getState()._pushState('Redimensionnement');
                                        }
                                    }, 0);
                                }}
                                className="group"
                                onMouseEnter={(e) => {
                                    if (!isSelected) {
                                        const inner = e.currentTarget.querySelector('[data-hover-border]');
                                        if (inner) inner.style.opacity = '1';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSelected) {
                                        const inner = e.currentTarget.querySelector('[data-hover-border]');
                                        if (inner) inner.style.opacity = '0';
                                    }
                                }}
                            >
                                <div
                                    data-rotated-inner="true"
                                    className="w-full h-full relative"
                                    id={`rnd-${el.id}`}
                                    // Curseur toujours « move ». AVANT :
                                    // `el.locked ? 'default' : 'move'`, dont la branche
                                    // 'default' ne pouvait jamais s'afficher — un calque
                                    // verrouillé reçoit `pointerEvents: 'none'` sur son
                                    // wrapper Rnd (cf. plus haut), il n'est donc jamais
                                    // survolé et son curseur n'est jamais consulté. Le test
                                    // lisait de plus `el.locked` là où le garde-fou du
                                    // glissement lit isLeafLocked() : les deux notions
                                    // divergeaient sur l'enfant d'un groupe verrouillé, à qui
                                    // il promettait « move » sans qu'il puisse bouger.
                                    style={{ transform: `rotate(${el.rotation || 0}deg)`, cursor: 'move', opacity: el.opacity !== undefined ? el.opacity / 100 : 1, mixBlendMode: el.blendMode || 'normal', ...getShadowStyle(el) }}
                                    onMouseDown={(e) => handleDragStart(e, el)}
                                    // VITAL: le stopper pour empêcher le canvas de nettoyer la sélection
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (e.shiftKey) {
                                            // Le clic ne RETIRE que ce qui était déjà sélectionné avant
                                            // l'appui, et jamais au bout d'un glissement : déplacer une
                                            // sélection à la souris ne doit pas en amputer un membre.
                                            // Un calque qui n'y était pas, l'appui l'a déjà ajouté.
                                            const g = gesteMajRef.current;
                                            if (g.id === el.id && g.etaitSelectionne && !g.aBouge) {
                                                selectElement(el.id, true);
                                            }
                                        }
                                    }}
                                    // Double-clic sur un élément GROUPÉ → on « entre » dans le groupe
                                    // (sélection directe de l'enfant). Le texte gère son propre
                                    // double-clic (édition) en amont via TextLayer (stopPropagation).
                                    onDoubleClick={(e) => {
                                        if (el.parentId) {
                                            e.stopPropagation();
                                            selectElement(el.id);
                                        }
                                    }}
                                >
                                    {/* Poignées d'angle pour calque PIVOTÉ — rendues DANS le
                                        conteneur tourné pour suivre la rotation visuelle.
                                        Géométrie custom : cf. handleRotatedResizeStart. */}
                                    {isSelected && !el.locked && !isEditingThis && (el.rotation || 0) !== 0 && (
                                        ['tl', 'tr', 'bl', 'br'].map((corner) => (
                                            <div key={corner}
                                                data-no-export="true"
                                                onMouseDown={(e) => handleRotatedResizeStart(e, el, corner)}
                                                style={{
                                                    position: 'absolute',
                                                    [corner.includes('t') ? 'top' : 'bottom']: `${-5 / canvas.scale}px`,
                                                    [corner.includes('l') ? 'left' : 'right']: `${-5 / canvas.scale}px`,
                                                    width: `${10 / canvas.scale}px`,
                                                    height: `${10 / canvas.scale}px`,
                                                    background: '#ffffff',
                                                    border: `${1.5 / canvas.scale}px solid #eab308`,
                                                    borderRadius: `${2 / canvas.scale}px`,
                                                    boxShadow: '0 0 4px rgba(0,0,0,0.4)',
                                                    cursor: 'grabbing',
                                                    zIndex: 40,
                                                    pointerEvents: 'auto',
                                                }}
                                            />
                                        ))
                                    )}
                                    {/* Hover border — SVG for sub-pixel precision, hidden by default */}
                                    {!isSelected && (
                                        <svg data-hover-border="true"
                                            className="absolute inset-0 pointer-events-none overflow-visible z-40"
                                            width="100%" height="100%"
                                            style={{ opacity: 0, transition: 'opacity 0.15s' }}>
                                            <rect x="0" y="0" width="100%" height="100%"
                                                fill="none" stroke="rgba(255,255,255,0.4)"
                                                strokeWidth={1 / canvas.scale} />
                                        </svg>
                                    )}
                                    {renderElementContent(el)}

                                    {/* Ombre INTERNE. AVANT : le mode « Interne » de l'inspecteur
                                        n'avait aucun effet sur une forme ou une image. Elle est posée
                                        ici, au-dessus du contenu : un box-shadow inset porté par le
                                        conteneur serait peint SOUS ses enfants, donc invisible dès que
                                        le calque a un fond ou une image opaque. Le rayon suit la
                                        géométrie du calque pour que l'ombre longe le bord réel. */}
                                    {(() => {
                                        const styleInterne = getInsetShadowStyle(el);
                                        if (!styleInterne.boxShadow) return null;
                                        const rayon = (el.type === 'shape' && el.shapeType === 'circle')
                                            ? '50%'
                                            : (el.borderRadius ? `${el.borderRadius}px` : undefined);
                                        return (
                                            <div
                                                data-inset-shadow="true"
                                                className="absolute inset-0 pointer-events-none z-30"
                                                style={{ borderRadius: rayon, ...styleInterne }}
                                            />
                                        );
                                    })()}

                                    {/* Cadre de sélection et Repères de précision maximale via SVG Vectoriel.
                                        Masqué pendant l'édition inline (sinon le rectangle jaune cache le caret) */}
                                    {isSelected && !isEditingThis && (
                                        <svg
                                            className="absolute inset-0 pointer-events-none overflow-visible z-50"
                                            width="100%" height="100%"
                                        >
                                            {/* Contour géométrique strict (le stroke SVG est toujours centré sur le Path parfait) */}
                                            <rect 
                                                x="0" y="0" width="100%" height="100%" 
                                                fill="none" 
                                                stroke={el.locked ? '#ef4444' : '#eab308'} 
                                                strokeWidth={1 / canvas.scale} 
                                            />

                                            {!el.locked && (
                                                <>
                                                    {/* Repère Haut */}
                                                    <svg x="50%" y="0" overflow="visible">
                                                        <line x1="0" y1={-3 / canvas.scale} x2="0" y2={3 / canvas.scale} stroke="#eab308" strokeWidth={1 / canvas.scale} />
                                                    </svg>
                                                    
                                                    {/* Repère Bas */}
                                                    <svg x="50%" y="100%" overflow="visible">
                                                        <line x1="0" y1={-3 / canvas.scale} x2="0" y2={3 / canvas.scale} stroke="#eab308" strokeWidth={1 / canvas.scale} />
                                                    </svg>

                                                    {/* Repère Gauche */}
                                                    <svg x="0" y="50%" overflow="visible">
                                                        <line x1={-3 / canvas.scale} y1="0" x2={3 / canvas.scale} y2="0" stroke="#eab308" strokeWidth={1 / canvas.scale} />
                                                    </svg>
                                                    
                                                    {/* Repère Droite */}
                                                    <svg x="100%" y="50%" overflow="visible">
                                                        <line x1={-3 / canvas.scale} y1="0" x2={3 / canvas.scale} y2="0" stroke="#eab308" strokeWidth={1 / canvas.scale} />
                                                    </svg>

                                                    {/* Croix centrale de gravité */}
                                                    <svg x="50%" y="50%" overflow="visible" opacity="0.6" style={{ mixBlendMode: 'difference' }}>
                                                        <line x1={-4 / canvas.scale} y1="0" x2={4 / canvas.scale} y2="0" stroke="#eab308" strokeWidth={1 / canvas.scale} />
                                                        <line x1="0" y1={-4 / canvas.scale} x2="0" y2={4 / canvas.scale} stroke="#eab308" strokeWidth={1 / canvas.scale} />
                                                    </svg>

                                                    {/* Poignées de Redimensionnement (Carrés Vectoriels strictement superposés sur la grille) */}
                                                    <svg x="0" y="0" overflow="visible">
                                                        <rect x={-3 / canvas.scale} y={-3 / canvas.scale} width={6 / canvas.scale} height={6 / canvas.scale} fill="white" stroke="#eab308" strokeWidth={1 / canvas.scale} />
                                                    </svg>
                                                    <svg x="100%" y="0" overflow="visible">
                                                        <rect x={-3 / canvas.scale} y={-3 / canvas.scale} width={6 / canvas.scale} height={6 / canvas.scale} fill="white" stroke="#eab308" strokeWidth={1 / canvas.scale} />
                                                    </svg>
                                                    <svg x="0" y="100%" overflow="visible">
                                                        <rect x={-3 / canvas.scale} y={-3 / canvas.scale} width={6 / canvas.scale} height={6 / canvas.scale} fill="white" stroke="#eab308" strokeWidth={1 / canvas.scale} />
                                                    </svg>
                                                    <svg x="100%" y="100%" overflow="visible">
                                                        <rect x={-3 / canvas.scale} y={-3 / canvas.scale} width={6 / canvas.scale} height={6 / canvas.scale} fill="white" stroke="#eab308" strokeWidth={1 / canvas.scale} />
                                                    </svg>
                                                </>
                                            )}
                                        </svg>
                                    )}

                                    {/* Poignées de Rotation — décalées en diagonale, HORS de la zone des handles
                                        de redimensionnement d'angle (qui font ±7/scale). On les place de
                                        -22/scale à -8/scale : pas de chevauchement → curseur 'rotate' stable.

                                        Retirées sous la Gomme et sous l'outil Zone, comme les poignées de
                                        redimensionnement (cf. enableResizing) : elles débordent le cadre, si
                                        bien qu'un geste parti d'un angle faisait PIVOTER le calque en pleine
                                        séance de gommage ou de tracé. Retirées aussi pendant l'édition d'un
                                        texte, où le geste appartient au curseur. */}
                                    {isSelected && !el.locked && !isEditingThis
                                        && outilEffectif !== 'eraser' && outilEffectif !== 'marquee' && (() => {
                                        const rotSize = 14 / canvas.scale;
                                        const rotOffset = 22 / canvas.scale;
                                        const rotBase = { width: `${rotSize}px`, height: `${rotSize}px`, pointerEvents: 'auto' };
                                        return (
                                            <>
                                                <div onMouseDown={(e) => handleRotateStart(e, el)} className="absolute z-50 rounded-full cursor-rotate" title="Pivoter" style={{ ...rotBase, top: `${-rotOffset}px`, left: `${-rotOffset}px` }} />
                                                <div onMouseDown={(e) => handleRotateStart(e, el)} className="absolute z-50 rounded-full cursor-rotate" title="Pivoter" style={{ ...rotBase, top: `${-rotOffset}px`, right: `${-rotOffset}px` }} />
                                                <div onMouseDown={(e) => handleRotateStart(e, el)} className="absolute z-50 rounded-full cursor-rotate" title="Pivoter" style={{ ...rotBase, bottom: `${-rotOffset}px`, left: `${-rotOffset}px` }} />
                                                <div onMouseDown={(e) => handleRotateStart(e, el)} className="absolute z-50 rounded-full cursor-rotate" title="Pivoter" style={{ ...rotBase, bottom: `${-rotOffset}px`, right: `${-rotOffset}px` }} />
                                            </>
                                        );
                                    })()}
                                </div>
                                {/* Mini-cartouche flottant de redimensionnement précis.
                                    Apparait sur les calques rotates (les handles re-resizable
                                    sont desactives car ne suivent pas la rotation visuelle).
                                    L'utilisateur peut alors redimensionner via les boutons +/-
                                    avec saisie au clavier. Rendu au niveau du wrapper Rnd
                                    (hors data-rotated-inner) pour ne pas etre lui-meme rotate. */}
                                {isSelected && !el.locked && !isEditingThis && (el.rotation || 0) !== 0 && (
                                    <DimensionsHUD el={el} canvas={canvas} updateElement={updateElement} />
                                )}
                            </Rnd>
                        );
                    })}

                    {/* 3. Cadre de sélection AU-DESSUS de la pile (sans les poignées).
                        Le calque sélectionné garde sa place dans l'ordre de peinture : ce cadre
                        seul est redessiné par-dessus les calques qui le recouvrent, sous les repères
                        (z 48 et plus). Pointeur transparent : il ne vole aucun clic. */}
                    {orderedLeaves(elements).map(({ el, effHidden }) => {
                        if (effHidden || el.type === 'guide' || el.type === 'group') return null;
                        const isSelected = selectedElementIds.includes(el.id) || ancestorGroupSelected(el);
                        if (!isSelected || el.id === editingId) return null;
                        return (
                            <div key={`cadre-${el.id}`} data-no-export="true"
                                className="absolute pointer-events-none"
                                style={{
                                    left: el.x + bleed, top: el.y + bleed, width: el.width, height: el.height,
                                    transform: (el.rotation || 0) !== 0 ? `rotate(${el.rotation}deg)` : undefined,
                                    zIndex: 45,
                                }}>
                                <svg className="absolute inset-0 overflow-visible" width="100%" height="100%">
                                    <rect x="0" y="0" width="100%" height="100%" fill="none"
                                        stroke={el.locked ? '#ef4444' : '#eab308'} strokeWidth={1 / canvas.scale} />
                                </svg>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
